import { forwardRef, TouchEvent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { ANIMATION_DURATION, handleRefSplit, isMobileDevice } from '@optimus-web/common';
import { ActivableRenderer, Portal, useId } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { useModalFocus, useLockScroll } from '../hooks';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { VisuallyHidden } from '../helpers';
import { DrawerFooter } from './DrawerFooter';
import {
    DrawerBackdrop,
    DrawerCloseHandler,
    DrawerContainer,
    DrawerContent,
    DrawerDragBar,
    DrawerWrapper,
    getMinSwipeDistance,
} from './style';
import { CloseHandlerVariant, DrawerProps } from './types';
import { useDrawerHeight } from './useDrawerHeight';
import { usePreventPullToRefresh } from './usePreventPullToRefresh';

const DrawerActivatorFunction = ActivableRenderer<HTMLDivElement, DrawerProps>({
    activePropName: 'open',
    delay: ANIMATION_DURATION.default,
});

type VoidFn = () => void;

interface CloseHandlerProps {
    onClick?: VoidFn;
    onBackClick?: VoidFn;
    backButtonLabel?: string;
    closeButtonLabel?: string;
}

const getDrawerCloseHandler = (closeHandler: CloseHandlerVariant, props: CloseHandlerProps) => {
    const closeButton = (
        <IconButton
            icon={IconName.CrossClose}
            variant="ghost"
            iconSize="medium"
            onClick={props.onClick}
            type="button"
            accessibleText={props.closeButtonLabel}
        />
    );

    const backButton = (
        <IconButton
            icon={IconName.ArrowLeft}
            variant="ghost"
            iconSize="medium"
            onClick={closeHandler === 'backAndClose' ? props.onBackClick : props.onClick}
            type="button"
            accessibleText={props.backButtonLabel}
        />
    );

    switch (closeHandler) {
        case 'none':
            return null;
        case 'close':
            return closeButton;
        case 'back':
            return backButton;
        case 'backAndClose':
            return (
                <Stack fill distribution="space-between">
                    {backButton}
                    {closeButton}
                </Stack>
            );
        case 'drag':
            return <DrawerDragBar />;
    }
};

const DrawerComponent = forwardRef<HTMLDivElement, DrawerProps>(
    (
        {
            children,
            footer,
            height,
            open,
            onClose,
            onBackClick,
            portalContainerId,
            closeHandler = 'drag',
            lockScroll,
            withoutPadding = false,
            anchorElement,
            defaultFocusElement,
            accessibleTitle,
            ...other
        },
        ref
    ) => {
        const [touchStart, setTouchStart] = useState<number | null>(null);
        const [touchEnd, setTouchEnd] = useState<number | null>(null);
        const [swipingInProgress, setSwipingInProgress] = useState(false);
        const [drawerIsCollapsed, setDrawerIsCollapsed] = useState(false);

        const { t } = useLocalization();
        const closeButtonLabel = `${t(OptimusTextKey.CloseDialog)} ${String(accessibleTitle)}`;
        const dragButtonLabel = `${t(OptimusTextKey.ClickToClose)} ${String(accessibleTitle)}`;
        const backButtonLabel = `${t(OptimusTextKey.Back)}`;

        const drawerTitleId = useId('drawer-title');
        const theme = useTheme();
        const drawerHeight = useDrawerHeight();

        const drawerRef = useRef<HTMLDivElement>(null);
        const contentRef = useRef<HTMLDivElement>(null);

        useLockScroll(open, lockScroll);
        usePreventPullToRefresh(!!(open && isMobileDevice()), contentRef);
        useModalFocus({
            open,
            componentRef: drawerRef,
            anchorElement,
            defaultFocusElement,
        });

        const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
            setTouchEnd(null);
            setTouchStart(e.targetTouches[0].clientY);
        };

        const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
            setTouchEnd(e.targetTouches[0].clientY);
        };

        const onTouchEnd = () => {
            if (touchStart === null || touchEnd === null) return;
            const distance = touchEnd - touchStart;
            const isSwipe = getMinSwipeDistance({ theme }) < distance;

            if (drawerRef.current) {
                drawerRef.current.style.transform = `translate3d(0, ${isSwipe ? '100%' : '0'}, 0)`;
            }

            if (isSwipe && onClose) {
                setDrawerIsCollapsed(true);
                onClose();
            }
        };

        const swipeDistance = () => {
            if (!touchStart || !touchEnd || touchEnd - touchStart < 0) return 0;
            if (drawerRef.current) {
                setSwipingInProgress(true);
                drawerRef.current.style.transform = `translate3d(0, ${touchEnd - touchStart}px, 0)`;
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === KeyValue.Enter || event.key === KeyValue.Spacebar) {
                onClose?.();
            }
        };

        useEffect(() => {
            if (!drawerIsCollapsed && isMobileDevice()) {
                swipeDistance();
            }
        });

        return (
            <Portal containerId={portalContainerId} {...other}>
                <DrawerWrapper
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={accessibleTitle ? drawerTitleId : undefined}
                >
                    {open && <DrawerBackdrop onClick={onClose} />}
                    <DrawerContainer
                        ref={handleRefSplit({ ownRef: drawerRef, forwardedRef: ref })}
                        open={open}
                        height={height || drawerHeight}
                        maxHeight={drawerHeight}
                        data-test-drawer
                        swipingInProgress={swipingInProgress}
                    >
                        {accessibleTitle && <VisuallyHidden id={drawerTitleId}>{accessibleTitle}</VisuallyHidden>}
                        {closeHandler !== 'none' && (
                            <DrawerCloseHandler
                                onClick={closeHandler === 'drag' ? onClose : undefined}
                                onKeyDown={closeHandler === 'drag' ? handleKeyDown : undefined}
                                {...{
                                    closeHandler,
                                    ...(closeHandler === 'drag' && {
                                        tabIndex: 0,
                                        role: 'button',
                                        'aria-label': dragButtonLabel,
                                    }),
                                }}
                                {...(isMobileDevice() && {
                                    onTouchStart,
                                    onTouchMove,
                                    onTouchEnd,
                                })}
                                data-test-drawer-close-handler
                            >
                                {getDrawerCloseHandler(closeHandler, {
                                    onClick: onClose,
                                    onBackClick,
                                    backButtonLabel,
                                    closeButtonLabel,
                                })}
                            </DrawerCloseHandler>
                        )}
                        <DrawerContent
                            ref={contentRef}
                            withoutPadding={withoutPadding}
                            data-test-drawer-content
                            data-drawer-content
                            closeHandler={closeHandler}
                        >
                            {children}
                        </DrawerContent>
                        {footer ? <DrawerFooter {...footer} /> : null}
                    </DrawerContainer>
                </DrawerWrapper>
            </Portal>
        );
    }
);

export const Drawer = DrawerActivatorFunction(DrawerComponent);
